import type { BillingFrequencyValue, PaymentMethodValue } from '@my/directory-api';
import {
  useCargoDefinitions,
  useChargeCostDefinitions,
  useCreateCustomerAdditionalCharge,
  useCreateCustomerFuelSurcharge,
  useCreateCustomerRateSheet,
  useCreateCustomerRouteSheet,
  useCustomerRateCard,
  useDeleteCustomerRateCardItem,
  useUpdateCustomerFuelSurcharge,
  useUpdateCustomerRateSheet,
  useUpdateCustomerRouteSheet,
} from '@my/tms-api/hooks';
import type { CustomerFuelSurcharge, RateCardItemKindValue } from '@my/tms-api';
import { cn, ConfirmDialog, ErrorBanner, FormSection, showToast } from '@my/ui';
import { findCurrency } from '@my/platform/options';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PartyListMode } from './PartyListPage';
import {
  TransportChargeRuleForm,
  type TransportSheetEdit,
} from './rates-charges/TransportChargeRuleForm';
import { FuelSurchargeForm } from './rates-charges/FuelSurchargeForm';
import { AdditionalsForm } from './rates-charges/AdditionalsForm';
import { mapRateCard } from './rates-charges/liveMappings';
import {
  laneMatchModeLabel,
  PERIOD_STATUS_DOT,
  type PeriodStatus,
} from './rates-charges/ratesChargesMock';

export interface PartyRatesAndChargesSectionProps {
  organisationId: string;
  customerPartyId: string;
  /** Party's transactional currency — drives money symbols + the header meta. */
  currencyIsoCode: string;
  billingFrequency: BillingFrequencyValue;
  paymentMethod: PaymentMethodValue;
  mode: PartyListMode;
}

/**
 * Party-level Rates & Charges — three live grids (Transport / Fuel / Additionals),
 * read from the customer rate card and written via the create mutations. Cargo +
 * charge names resolve from the cached org catalogues; status + date labels are
 * derived from the effective windows.
 */
export function PartyRatesAndChargesSection({
  organisationId,
  customerPartyId,
  currencyIsoCode,
  billingFrequency,
  paymentMethod,
  mode: _mode,
}: PartyRatesAndChargesSectionProps) {
  const { t } = useTranslation(['parties', 'common']);

  const symbol = findCurrency(currencyIsoCode)?.symbol ?? '€';

  const cardQuery = useCustomerRateCard(organisationId, customerPartyId);
  const cargoQuery = useCargoDefinitions(organisationId);
  const chargeQuery = useChargeCostDefinitions(organisationId);

  // Project the live card → display rows (names resolved, money formatted).
  const { engineRules, routeRules, fuel, additionals } = useMemo(
    () => mapRateCard(cardQuery.data, cargoQuery.data ?? [], chargeQuery.data ?? [], symbol),
    [cardQuery.data, cargoQuery.data, chargeQuery.data, symbol],
  );

  const createRateSheet = useCreateCustomerRateSheet();
  const createRouteSheet = useCreateCustomerRouteSheet();
  const updateRateSheet = useUpdateCustomerRateSheet();
  const updateRouteSheet = useUpdateCustomerRouteSheet();
  const createFuel = useCreateCustomerFuelSurcharge();
  const updateFuel = useUpdateCustomerFuelSurcharge();
  const createAdditional = useCreateCustomerAdditionalCharge();
  const deleteItem = useDeleteCustomerRateCardItem();

  const [addingTransport, setAddingTransport] = useState(false);
  const [editingTransport, setEditingTransport] = useState<TransportSheetEdit | null>(null);
  const [addingFuel, setAddingFuel] = useState(false);
  const [editingFuel, setEditingFuel] = useState<CustomerFuelSurcharge | null>(null);
  const [addingAdditionals, setAddingAdditionals] = useState(false);

  // Raw fuel surcharges by id — the display rows are formatted strings, so the
  // edit form prefills from the underlying entity.
  const fuelById = useMemo(
    () => new Map((cardQuery.data?.fuelSurcharges ?? []).map((f) => [f.id, f] as const)),
    [cardQuery.data],
  );

  // Raw sheets by id — the display rows are formatted strings, so the edit
  // form prefills from the underlying entities.
  const rateSheetById = useMemo(
    () => new Map((cardQuery.data?.rateSheets ?? []).map((r) => [r.id, r] as const)),
    [cardQuery.data],
  );
  const routeSheetById = useMemo(
    () => new Map((cardQuery.data?.routeSheets ?? []).map((r) => [r.id, r] as const)),
    [cardQuery.data],
  );

  const closeTransportForm = () => {
    setAddingTransport(false);
    setEditingTransport(null);
  };

  // Every trash button routes through one ConfirmDialog — the suite's modal,
  // not the browser's. The row supplies the wording; the dialog supplies the
  // yes/no and the in-flight state.
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const askDeleteRateSheet = (id: string, label: string) =>
    setPendingDelete({
      kind: 'RateSheet',
      id,
      title: 'Delete rate sheet?',
      message: `The rate sheet for ${label} will be removed. This cannot be undone.`,
      done: `Rate sheet for ${label} removed.`,
      failed: 'Could not delete the rate sheet.',
    });

  // Delete takes the whole LANE, not the one row — the grid shows a row per
  // cargo type, so the confirm spells out how many go with it.
  const askDeleteRouteSheet = (sheetId: string, lane: string, lineCount: number) =>
    setPendingDelete({
      kind: 'RouteSheet',
      id: sheetId,
      title: 'Delete route charge?',
      message:
        lineCount > 1
          ? `The route charge for ${lane} will be removed, along with all ${lineCount} cargo types priced on that lane. This cannot be undone.`
          : `The route charge for ${lane} will be removed. This cannot be undone.`,
      done: `Route charge for ${lane} removed.`,
      failed: 'Could not delete the route charge.',
    });

  const askDeleteFuel = (id: string, name: string) =>
    setPendingDelete({
      kind: 'FuelSurcharge',
      id,
      title: 'Delete fuel surcharge?',
      message: `“${name}” will be removed. This cannot be undone.`,
      done: `Fuel surcharge “${name}” removed.`,
      failed: 'Could not delete the fuel surcharge.',
    });

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { kind, id, done, failed } = pendingDelete;
    try {
      await deleteItem.mutateAsync({ kind, id, organisationId, customerPartyId });
      // Close a form still open on the row that just went.
      if (editingTransport?.sheet.id === id) setEditingTransport(null);
      if (editingFuel?.id === id) setEditingFuel(null);
      setPendingDelete(null);
      showToast({ type: 'success', message: done });
    } catch (e) {
      setPendingDelete(null);
      onError(e, failed);
    }
  };

  const onError = (err: unknown, fallback: string) =>
    showToast({ type: 'error', message: err instanceof Error ? err.message : fallback });

  const meta = (
    <span className="text-[11px] text-slate-400">
      {currencyIsoCode} · {billingFrequency} · {paymentMethod}
    </span>
  );

  return (
    <FormSection title={t('parties:ratesAndCharges.title')} columns={1} actions={meta}>
      <div className="space-y-3">
        {cardQuery.isError && (
          <ErrorBanner title="Failed to load rate card" message={cardQuery.error.message} />
        )}

        {/* ── Transport Charges ─────────────────────────────────────────── */}
        <Grid
          title="Transport Charges"
          onAdd={() => {
            setEditingTransport(null);
            setAddingTransport((v) => !v);
          }}
        >
          {(addingTransport || editingTransport) && (
            <div className="border-b border-slate-200 bg-slate-50 p-3">
              <TransportChargeRuleForm
                key={editingTransport?.sheet.id ?? 'new'}
                organisationId={organisationId}
                customerPartyId={customerPartyId}
                currencyIsoCode={currencyIsoCode}
                currencySymbol={symbol}
                editing={editingTransport}
                onCancel={closeTransportForm}
                onSaveEngine={async (req) => {
                  try {
                    await createRateSheet.mutateAsync(req);
                    closeTransportForm();
                    showToast({ type: 'success', message: 'Rate sheet added.' });
                  } catch (e) {
                    onError(e, 'Could not add the rate sheet.');
                  }
                }}
                onSaveRoute={async (req) => {
                  try {
                    await createRouteSheet.mutateAsync(req);
                    closeTransportForm();
                    showToast({ type: 'success', message: 'Route sheet added.' });
                  } catch (e) {
                    onError(e, 'Could not add the route sheet.');
                  }
                }}
                onUpdateEngine={async (req) => {
                  try {
                    await updateRateSheet.mutateAsync({
                      request: req,
                      organisationId,
                      customerPartyId,
                    });
                    closeTransportForm();
                    showToast({ type: 'success', message: 'Rate sheet updated.' });
                  } catch (e) {
                    onError(e, 'Could not update the rate sheet.');
                  }
                }}
                onUpdateRoute={async (req) => {
                  try {
                    await updateRouteSheet.mutateAsync({
                      request: req,
                      organisationId,
                      customerPartyId,
                    });
                    closeTransportForm();
                    showToast({ type: 'success', message: 'Route charge updated.' });
                  } catch (e) {
                    onError(e, 'Could not update the route charge.');
                  }
                }}
              />
            </div>
          )}

          <SubLabel>Rate engine rules</SubLabel>
          <Table
            head={[
              'Cargo type',
              'Applies to',
              'Method',
              'Rate',
              'Min charge',
              'Active from / to',
              'Status',
              '',
            ]}
          >
            {engineRules.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <Td>
                  <div className="font-semibold text-slate-900">{r.cargoLabel}</div>
                  <div className="flex flex-wrap items-center gap-1">
                    <RuleBadge tone="engine">Rate sheet</RuleBadge>
                    {!r.autoApply && <RuleBadge tone="agreed">Ask first</RuleBadge>}
                  </div>
                </Td>
                <Td>
                  <Chips items={r.appliesTo} />
                </Td>
                <Td className="text-slate-500">{r.methodLabel}</Td>
                <Td className="tabular-nums text-slate-700">{r.rateLabel}</Td>
                <Td className="tabular-nums text-slate-700">{r.minCharge}</Td>
                <Td className="text-slate-500">
                  <PeriodCell fromLabel={r.fromLabel} toLabel={r.toLabel} />
                </Td>
                <Td>
                  <StatusCell status={r.status} />
                </Td>
                <Td className="text-right">
                  <RowActions
                    label={r.cargoLabel}
                    canEdit={rateSheetById.has(r.id)}
                    onEdit={() => {
                      const sheet = rateSheetById.get(r.id);
                      if (!sheet) return;
                      setAddingTransport(false);
                      setEditingTransport({ kind: 'rateSheet', sheet });
                    }}
                    onDelete={() => askDeleteRateSheet(r.id, r.cargoLabel)}
                  />
                </Td>
              </tr>
            ))}
          </Table>

          <SubLabel>Route charges</SubLabel>
          <Table
            head={['Lane', 'Applies to', 'Charge', 'Detail', 'Active from / to', 'Status', '']}
          >
            {routeRules.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <Td>
                  <div className="font-semibold text-slate-900">{r.lane}</div>
                  <div className="flex flex-wrap items-center gap-1">
                    <RuleBadge tone="route">Route sheet</RuleBadge>
                    <RuleBadge tone={r.matchMode === 'Contains' ? 'agreed' : 'engine'}>
                      {laneMatchModeLabel(r.matchMode)}
                    </RuleBadge>
                    {/* Only the exception is worth a badge — automatic is the
                        default, and badging every row would say nothing. */}
                    {!r.autoApply && <RuleBadge tone="agreed">Ask first</RuleBadge>}
                  </div>
                </Td>
                <Td>
                  <Chips items={r.appliesTo} />
                </Td>
                <Td className="font-semibold tabular-nums text-slate-900">{r.charge}</Td>
                <Td className="text-slate-500">{r.detail || '—'}</Td>
                <Td className="text-slate-500">
                  <PeriodCell fromLabel={r.fromLabel} toLabel={r.toLabel} />
                </Td>
                <Td>
                  <StatusCell status={r.status} />
                </Td>
                <Td className="text-right">
                  <RowActions
                    label={r.lane}
                    canEdit={routeSheetById.has(r.sheetId)}
                    onEdit={() => {
                      const sheet = routeSheetById.get(r.sheetId);
                      if (!sheet) return;
                      setAddingTransport(false);
                      setEditingTransport({ kind: 'routeSheet', sheet });
                    }}
                    onDelete={() => askDeleteRouteSheet(r.sheetId, r.lane, r.sheetLineCount)}
                  />
                </Td>
              </tr>
            ))}
          </Table>
        </Grid>

        {/* ── Fuel Surcharge ────────────────────────────────────────────── */}
        <Grid
          title="Fuel Surcharge"
          onAdd={() => {
            setEditingFuel(null);
            setAddingFuel((v) => !v);
          }}
        >
          {(addingFuel || editingFuel) && (
            <div className="border-b border-slate-200 bg-slate-50 p-3">
              <FuelSurchargeForm
                key={editingFuel?.id ?? 'new'}
                organisationId={organisationId}
                customerPartyId={customerPartyId}
                existing={fuel}
                editing={editingFuel}
                onCancel={() => {
                  setAddingFuel(false);
                  setEditingFuel(null);
                }}
                onSave={async (req) => {
                  try {
                    await createFuel.mutateAsync(req);
                    setAddingFuel(false);
                    showToast({
                      type: 'success',
                      message: `Fuel surcharge "${req.name}" added.`,
                    });
                  } catch (e) {
                    onError(e, 'Could not add the fuel surcharge.');
                  }
                }}
                onUpdate={async (req) => {
                  try {
                    await updateFuel.mutateAsync({ request: req, organisationId, customerPartyId });
                    setEditingFuel(null);
                    showToast({
                      type: 'success',
                      message: `Fuel surcharge "${req.name}" updated.`,
                    });
                  } catch (e) {
                    onError(e, 'Could not update the fuel surcharge.');
                  }
                }}
              />
            </div>
          )}
          <Table head={['Name', 'Territory', 'Rate', 'Active from / to', 'Status', '']}>
            {fuel.map((f) => (
              <tr key={f.id} className="hover:bg-slate-50">
                <Td className="font-semibold text-slate-900">{f.name}</Td>
                <Td>
                  <span className="inline-flex items-center whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                    {f.territory}
                  </span>
                </Td>
                <Td className="font-semibold tabular-nums text-slate-900">{f.rate}</Td>
                <Td className="text-slate-500">
                  <PeriodCell fromLabel={f.fromLabel} toLabel={f.toLabel} />
                </Td>
                <Td>
                  <StatusCell status={f.status} />
                </Td>
                <Td className="text-right">
                  <RowActions
                    label={f.name}
                    canEdit={fuelById.has(f.id)}
                    onEdit={() => {
                      setAddingFuel(false);
                      setEditingFuel(fuelById.get(f.id) ?? null);
                    }}
                    onDelete={() => askDeleteFuel(f.id, f.name)}
                  />
                </Td>
              </tr>
            ))}
          </Table>
        </Grid>

        {/* ── Additionals ───────────────────────────────────────────────── */}
        <Grid title="Additionals" onAdd={() => setAddingAdditionals((v) => !v)}>
          {addingAdditionals && (
            <div className="border-b border-slate-200 bg-slate-50 p-3">
              <AdditionalsForm
                organisationId={organisationId}
                customerPartyId={customerPartyId}
                currencySymbol={symbol}
                currencyIsoCode={currencyIsoCode}
                existing={additionals}
                onCancel={() => setAddingAdditionals(false)}
                onSave={async (requests) => {
                  try {
                    for (const req of requests) await createAdditional.mutateAsync(req);
                    setAddingAdditionals(false);
                    showToast({
                      type: 'success',
                      message: `${requests.length} additional ${requests.length === 1 ? 'charge' : 'charges'} added.`,
                    });
                  } catch (e) {
                    onError(e, 'Could not add the additional charges.');
                  }
                }}
              />
            </div>
          )}
          <Table head={['Charge', 'Rate type', 'Rate', 'FS applicable']}>
            {additionals.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50">
                <Td>
                  <div className="font-semibold text-slate-900">{a.charge}</div>
                  <div className="text-[10px] text-slate-400">{a.sub}</div>
                </Td>
                <Td className="text-slate-500">{a.rateType}</Td>
                <Td className="tabular-nums text-slate-700">{a.rate}</Td>
                <Td>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                      a.fsApplicable
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {a.fsApplicable ? t('common:states.yes') : t('common:states.no')}
                  </span>
                </Td>
              </tr>
            ))}
          </Table>
        </Grid>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={pendingDelete?.title ?? ''}
        message={pendingDelete?.message}
        confirmLabel={t('common:actions.delete')}
        busy={deleteItem.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </FormSection>
  );
}

/** A trash click waiting on the confirm dialog — the row supplies the wording. */
interface PendingDelete {
  kind: RateCardItemKindValue;
  id: string;
  title: string;
  message: string;
  /** Toast once it's gone. */
  done: string;
  /** Toast fallback when the delete fails. */
  failed: string;
}

// ── Grid chrome ──────────────────────────────────────────────────────────────

/** One titled grid card with a dark header bar + an Add action. */
function Grid({
  title,
  onAdd,
  children,
}: {
  title: string;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <div className="flex items-center justify-between bg-slate-700 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-white">{title}</span>
        <button
          type="button"
          aria-label={`Add — ${title}`}
          title={`Add — ${title}`}
          onClick={onAdd}
          className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-emerald-400 transition-colors hover:bg-white/10 hover:text-emerald-300"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  );
}

/**
 * Per-row edit + delete. `canEdit` is false only when the row's underlying
 * entity isn't in the cached card (a display row we can't prefill a form
 * from) — delete still works, since it needs nothing but the id.
 */
function RowActions({
  label,
  canEdit,
  onEdit,
  onDelete,
}: {
  label: string;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        aria-label={`Edit ${label}`}
        title="Edit"
        disabled={!canEdit}
        onClick={onEdit}
        className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-blue-600 disabled:opacity-40"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label={`Delete ${label}`}
        title="Delete"
        onClick={onDelete}
        className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-400 hover:bg-red-50 hover:text-red-600"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Grey sub-section label inside a grid (e.g. "Rate engine rules"). */
function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-b border-slate-200 bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </div>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-slate-400">
          <tr className="border-b border-slate-200">
            {head.map((h, i) => (
              <th
                key={h}
                className={cn('px-3 py-1.5 font-medium', i === 0 ? 'text-left' : 'text-left')}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">{children}</tbody>
      </table>
    </div>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2 align-top', className)}>{children}</td>;
}

function Chips({ items }: { items: string[] }) {
  if (items.length === 0) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((c) => (
        <span
          key={c}
          className="inline-flex items-center whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600"
        >
          {c}
        </span>
      ))}
    </div>
  );
}

/** Effective window: "from → to", or "from" with a "No expiry" sub-line. */
function PeriodCell({ fromLabel, toLabel }: { fromLabel: string; toLabel: string }) {
  if (toLabel === 'No expiry') {
    return (
      <>
        <div>{fromLabel}</div>
        <div className="text-[10px] text-slate-400">No expiry</div>
      </>
    );
  }
  return (
    <span>
      {fromLabel} <span className="text-slate-300">→</span> {toLabel}
    </span>
  );
}

/** Status dot + label (Planned / Current / Prior). */
function StatusCell({ status }: { status: PeriodStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-600">
      <span className={cn('h-1.5 w-1.5 rounded-full', PERIOD_STATUS_DOT[status])} />
      {status}
    </span>
  );
}

function RuleBadge({
  tone,
  children,
}: {
  tone: 'engine' | 'route' | 'agreed';
  children: React.ReactNode;
}) {
  const cls =
    tone === 'engine'
      ? 'bg-blue-50 text-blue-700'
      : tone === 'route'
        ? 'bg-emerald-50 text-emerald-700'
        : 'bg-amber-50 text-amber-700';
  return (
    <span
      className={cn(
        'mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium',
        cls,
      )}
    >
      {children}
    </span>
  );
}
